import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {HTMLElement | null} */
  #accountEl = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#bindAccountDialogPosition();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#unbindAccountDialogPosition();
  }

  /**
   * Place the Shopify account sheet under the Sign In button (desktop).
   */
  #bindAccountDialogPosition() {
    const account = this.querySelector('shopify-account');
    if (!(account instanceof HTMLElement)) return;

    this.#accountEl = account;
    account.addEventListener('open', this.#positionAccountDialog);
    window.addEventListener('resize', this.#positionAccountDialog);
    document.querySelector('.page-wrapper')?.addEventListener('scroll', this.#positionAccountDialog, {
      passive: true,
    });
    this.#positionAccountDialog();
  }

  #unbindAccountDialogPosition() {
    this.#accountEl?.removeEventListener('open', this.#positionAccountDialog);
    window.removeEventListener('resize', this.#positionAccountDialog);
    document.querySelector('.page-wrapper')?.removeEventListener('scroll', this.#positionAccountDialog);
    this.#accountEl = null;
  }

  #positionAccountDialog = () => {
    const account = this.#accountEl;
    if (!(account instanceof HTMLElement)) return;

    const rect = account.getBoundingClientRect();
    const gap = 8;
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    const inlineEnd = isRtl ? rect.left : window.innerWidth - rect.right;

    account.style.setProperty('--account-dialog-top', `${Math.round(rect.bottom + gap)}px`);
    account.style.setProperty('--account-dialog-inline-end', `${Math.round(Math.max(8, inlineEnd))}px`);
  };

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
